import React from "react";
import Tippy from "@tippyjs/react";

interface Role {
  roleId: string;
  roleName: string;
}

const TruncatedTooltip = ({ text }: { text: string }) => {
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const el = spanRef.current;
    if (el) {
      setIsTruncated(el.scrollWidth > el.offsetWidth);
    }
  }, [text]);

  return isTruncated ? (
    <Tippy content={text} animation="fade" theme="light" placement="top">
      <span ref={spanRef} className="truncate block">
        {text}
      </span>
    </Tippy>
  ) : (
    <span ref={spanRef} className="truncate block">
      {text}
    </span>
  );
};
interface PermissionRoleValue {
  checked: boolean;
  isDisabled: boolean;
}

interface PermissionRole {
  roleId: string;
  roleName: string;
  values: PermissionRoleValue;
}

interface Permission {
  id: number;
  description?: string;
  roles: PermissionRole[];
}

interface CustomRenderSimpleTableViewProps {
  permissions: Permission[];
  roles: Role[];
  isEditMode: boolean;
  checkedState: Record<string, Record<string, boolean>>;
  handleCheckboxChange: (permId: number, roleId: string, checked: boolean) => void;
  renderCell?: (args: {
    permission: Permission;
    role: Role;
    checked: boolean;
    isDisabled: boolean;
    onChange: (checked: boolean) => void;
    isEditMode: boolean;
  }) => React.ReactNode;
}

const CustomRenderSimpleTableView: React.FC<CustomRenderSimpleTableViewProps> = ({
  permissions,
  roles,
  isEditMode,
  checkedState,
  handleCheckboxChange,
  renderCell,
}) => {
  return (
    <table className="w-full border border-stroke-light border-collapse border-spacing-0">
      <thead className=" sticky top-0 z-20">
        <tr className="bg-stroke-light ">
          <th className="text-left px-6 py-3 text-sm font-semibold text-text-default w-36 md:w-70 min-w-36 max-w-70 md:max-w-70 md:sticky left-0 bg-stroke-light border-r border-b border-stroke-light">
            Permissions
          </th>
          {roles.map((role) => (
            <th
              key={role.roleId}
              className="text-center px-6 py-3 text-sm font-semibold text-text-default whitespace-nowrap w-36 md:w-40 min-w-36 max-w-38 md:min-w-40 md:max-w-40 border-r border-b border-stroke-light"
            >
              <TruncatedTooltip text={role.roleName} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="pb-5">
        {permissions.map((perm) => (
          <tr key={perm.id} className="border-b border-stroke-light hover:bg-purple-50 group">
            <td className="text-sm text-text-default font-normal whitespace-nowrap w-36 md:w-64 min-w-36 max-w-70 md:min-w-70 md:max-w-70 sticky md:left-0 bg-white z-10 border-r border-b border-stroke-light">
              <p className="px-6 py-3 leading-normal">{perm.description}</p>
            </td>
            {roles.map((role) => {
              const roleObj = perm.roles.find((r) => r.roleId === role.roleId);
              const value = roleObj?.values;
              const checked = isEditMode ? checkedState?.[perm.id]?.[role.roleId] ?? value?.checked ?? false : value?.checked ?? false;
              const isDisabled = isEditMode ? value?.isDisabled : true;
              return (
                <td
                  key={role.roleId}
                  className="text-center px-6 py-3 w-36 md:w-40 min-w-36 max-w-38 md:min-w-40 md:max-w-40 border-r border-b border-stroke-light"
                >
                  {renderCell
                    ? renderCell({
                        permission: perm,
                        role,
                        checked,
                        isDisabled: !!isDisabled,
                        onChange: (checked: boolean) => {
                          if (isEditMode) {
                            handleCheckboxChange(perm.id, role.roleId, checked);
                          }
                        },
                        isEditMode,
                      })
                    : null}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default CustomRenderSimpleTableView;
